import React, { FunctionComponent, useRef } from 'react'
import { defineMessage, defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { View } from 'react-native'

import { noop } from 'lodash-es'
import { useTheme } from 'styled-components/native'

import { FavoritedStar, TertiaryIconButton, Tooltip } from '../../atoms'
import { ButtonSize } from '../../atoms/baseButton/BaseButton'
import { IS_WEB } from '../../constants'
import { tID } from '../../utils/utils'

export interface FavoritedProvidersStarIconButtonProps {
  onPressStarIconButton: () => void
  isFilled: boolean
  isNavBarButton?: boolean
  providerName?: string
  onIconHover?: () => void
  getHoverVisible?: (hover: boolean) => void
}

const AddOrRemoveFavoritedProviderTooltipMessage = defineMessage({
  defaultMessage: 'Add to favorites',
  description: 'Tooltip text for the star icon button that adds a provider to favorites',
})

const FavoritedProvidersPageTooltipMessage = defineMessage({
  defaultMessage: 'Favorite providers',
  description: 'Tooltip text for the star icon button that navigates the user to the favorited providers page',
})

const favoritedProviderAccessibilityLabels = defineMessages({
  addToFavorites: {
    defaultMessage: 'Add {providerName} to favorites',
    description: 'Accessibility label for star button to add provider to favorites',
  },
  removeFromFavorites: {
    defaultMessage: 'Remove {providerName} from favorites',
    description: 'Accessibility label for star button to remove provider from favorites',
  },
})

/**
 * Favorited Providers Star icon button with hover state and tooltip
 *
 * This icon is found on the provider cards for favoriting and unfavoriting providers
 * This icon is also used on the navbar to as a 1-click navigation to the favorited providers page
 *
 * @param onPressStarIconButton - function to call when the star icon is pressed, i.e. favorite/unfavorite provider or nav to favorited providers page
 * @param isFilled - boolean to determine if the star is filled (clicked state) or not, i.e. is currently favorited or currently on the favorited providers page
 * @param isNavBarButton - boolean to determine if the star is being used on the navbar or not
 * @param providerName - optional provider name for accessibility labels
 * @param getHoverVisible - optional callback to maintain parent component hover state when star is hovered
 * @param onIconHover - optional callback to track hover events
 */
export const FavoritedProvidersStarIconButton: FunctionComponent<FavoritedProvidersStarIconButtonProps> = ({
  onPressStarIconButton,
  isFilled,
  isNavBarButton = false,
  providerName = '',
  getHoverVisible = noop,
  onIconHover = noop,
}) => {
  const { colors } = useTheme()
  const { formatMessage } = useIntl()
  const { filled, filledAlternate, unfilled } = colors.components.favoritedProvidersStarIcon

  const iconColor = isFilled ? (isNavBarButton ? filledAlternate.fill : filled.fill) : unfilled.fill

  // ref to control tooltip state since hover behavior for the icon button can conflict with tooltip's native hover behavior
  const tooltipRef = useRef<{ show: () => void; hide: () => void }>(null)
  const tooltipMessage = isNavBarButton
    ? FavoritedProvidersPageTooltipMessage
    : AddOrRemoveFavoritedProviderTooltipMessage

  // Create accessibility label based on current state and provider name
  const accessibilityLabel = isNavBarButton
    ? formatMessage(FavoritedProvidersPageTooltipMessage)
    : providerName
    ? formatMessage(
        isFilled
          ? favoritedProviderAccessibilityLabels.removeFromFavorites
          : favoritedProviderAccessibilityLabels.addToFavorites,
        { providerName },
      )
    : formatMessage(AddOrRemoveFavoritedProviderTooltipMessage)

  return (
    <View testID={tID('FavoritedProvidersStarIconButton')}>
      {IS_WEB && !isFilled ? (
        <>
          <Tooltip
            ref={tooltipRef}
            content={<FormattedMessage {...tooltipMessage} />}
            placement={isNavBarButton ? 'bottom' : 'top'}
            hoverEnabled={true}
            style={
              IS_WEB
                ? {
                    outline: 'none',
                    position: 'absolute',
                    right: '50%',
                    marginTop: 40,
                    ...(isNavBarButton ? { top: '100%' } : { bottom: '100%' }),
                  }
                : {}
            }
          >
            <></>
          </Tooltip>
          <TertiaryIconButton
            onPress={onPressStarIconButton}
            iconColor={iconColor}
            leftIcon={<FavoritedStar />}
            accessibilityLabel={accessibilityLabel}
            size={ButtonSize.SMALL}
            onHoverIn={() => {
              onIconHover() // Track hover in event for tooltip
              getHoverVisible?.(true) // Maintain parent hover
              !isFilled ? tooltipRef.current?.show() : noop()
            }}
            onHoverOut={() => {
              getHoverVisible?.(true) // Maintain parent hover
              !isFilled ? tooltipRef.current?.hide() : noop()
            }}
          />
        </>
      ) : (
        <TertiaryIconButton
          onPress={onPressStarIconButton}
          iconColor={iconColor}
          leftIcon={<FavoritedStar isFilled={isFilled} />}
          accessibilityLabel={accessibilityLabel}
          onHoverIn={() => getHoverVisible?.(true)}
          onHoverOut={() => getHoverVisible?.(true)}
          size={ButtonSize.SMALL}
        />
      )}
    </View>
  )
}
